import {registerGlobal} from "./core.js";

class ColorDropdown {
    constructor(containerId, options = {}) {
        this.container = document.getElementById(containerId);
        this.options = Object.assign({
            value: "#ffffff",
            alpha: true,
            onChange: null
        }, options);

        this.value = this._parseColor(this.options.value);
        this.hsb = this._rgbToHsb(this.value);

        this._render();
        this._attachEvents();
        this._initCanvasPicker();
        this._updateUI();
    }

    _render() {
        this.container.classList.add("color-dropdown");
        this.container.innerHTML = `
      <div class="color-input">
        <div class="color-preview"></div>
        <input type="text" readonly value="${this._hexString(this.value)}" />
      </div>
      <div class="color-menu">
        <div class="color-area">
          <canvas class="color-mesh" width="180" height="140"></canvas>
          <canvas class="hue-bar" width="16" height="140"></canvas>
          <div class="color-marker"></div>
        </div>
        <div class="slider-group">
          <label>R</label><input type="range" min="0" max="255" data-type="r">
          <label>G</label><input type="range" min="0" max="255" data-type="g">
          <label>B</label><input type="range" min="0" max="255" data-type="b">
          ${this.options.alpha ? `<label>A</label><input type="range" min="0" max="255" data-type="a">` : ""}
        </div>
        <div class="slider-group">
          <label>H</label><input type="range" min="0" max="360" data-type="h">
          <label>S</label><input type="range" min="0" max="100" data-type="s">
          <label>B</label><input type="range" min="0" max="100" data-type="v">
        </div>
        <div class="hex-row"><input type="text" class="hex-input" maxlength="9"></div>
      </div>
    `;
    }

    _attachEvents() {
        const colorInput = this.container.querySelector(".color-input");
        const menu = this.container.querySelector(".color-menu");
        const hexInput = this.container.querySelector(".hex-input");

        colorInput.addEventListener("click", () => menu.classList.toggle("show"));
        document.addEventListener("click", (e) => {
            if (!this.container.contains(e.target)) menu.classList.remove("show");
        });

        hexInput.addEventListener("input", (e) => {
            const c = this._parseColor(e.target.value);
            if (c) {
                this.value = c;
                this.hsb = this._rgbToHsb(c);
                this._updateUI();
            }
        });

        this.container.querySelectorAll("input[type=range]").forEach(sl => {
            sl.addEventListener("input", () => this._handleSlider(sl));
        });
    }

    _initCanvasPicker() {
        const mesh = this.container.querySelector(".color-mesh");
        const hueBar = this.container.querySelector(".hue-bar");
        const marker = this.container.querySelector(".color-marker");
        const ctxMesh = mesh.getContext("2d");
        const ctxHue = hueBar.getContext("2d");

        const drawHueBar = () => {
            const hueGradient = ctxHue.createLinearGradient(0, 0, 0, hueBar.height);
            for (let i = 0; i <= 360; i += 10)
                hueGradient.addColorStop(i / 360, `hsl(${i}, 100%, 50%)`);
            ctxHue.fillStyle = hueGradient;
            ctxHue.fillRect(0, 0, hueBar.width, hueBar.height);
        };

        const drawMesh = (hue) => {
            const gradH = ctxMesh.createLinearGradient(0, 0, mesh.width, 0);
            gradH.addColorStop(0, "white");
            gradH.addColorStop(1, `hsl(${hue}, 100%, 50%)`);
            ctxMesh.fillStyle = gradH;
            ctxMesh.fillRect(0, 0, mesh.width, mesh.height);

            const gradV = ctxMesh.createLinearGradient(0, 0, 0, mesh.height);
            gradV.addColorStop(0, "rgba(0,0,0,0)");
            gradV.addColorStop(1, "black");
            ctxMesh.fillStyle = gradV;
            ctxMesh.fillRect(0, 0, mesh.width, mesh.height);
        };

        drawHueBar();
        drawMesh(this.hsb.h);

        const updateMarker = () => {
            marker.style.left = `${(this.hsb.s / 100) * mesh.width - 6}px`;
            marker.style.top = `${(1 - this.hsb.v / 100) * mesh.height - 6}px`;
        };

        const pickColor = (e) => {
            const rect = mesh.getBoundingClientRect();
            const s = Math.max(0, Math.min(100, ((e.clientX - rect.left) / mesh.width) * 100));
            const v = Math.max(0, Math.min(100, 100 - ((e.clientY - rect.top) / mesh.height) * 100));
            this.hsb.s = s;
            this.hsb.v = v;
            this.value = this._hsbToRgb(this.hsb);
            this._updateUI();
            updateMarker();
        };

        mesh.addEventListener("mousedown", (e) => {
            const move = (ev) => pickColor(ev);
            const stop = () => {
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", stop);
            };
            window.addEventListener("mousemove", move);
            window.addEventListener("mouseup", stop);
            pickColor(e);
        });

        hueBar.addEventListener("mousedown", (e) => {
            const rect = hueBar.getBoundingClientRect();
            const move = (ev) => {
                const y = Math.max(0, Math.min(rect.height, ev.clientY - rect.top));
                this.hsb.h = (y / rect.height) * 360;
                drawMesh(this.hsb.h);
                this.value = this._hsbToRgb(this.hsb);
                this._updateUI();
                updateMarker();
            };
            const stop = () => {
                window.removeEventListener("mousemove", move);
                window.removeEventListener("mouseup", stop);
            };
            window.addEventListener("mousemove", move);
            window.addEventListener("mouseup", stop);
            move(e);
        });

        updateMarker();
        this._drawMesh = drawMesh;
        this._updateMarker = updateMarker;
    }

    _handleSlider(slider) {
        const type = slider.dataset.type;
        if (type === "a") {
            this.value.a = Math.min(255, Math.max(0, parseInt(slider.value)));
            this._updateUI();
            return;
        }
        if ("rgb".includes(type)) {
            this.value[type] = parseFloat(slider.value);
            this._updateFromRGB();
            return;
        }
        if (["h", "s", "v"].includes(type)) {
            this.hsb[type] = parseFloat(slider.value);
            this.value = this._hsbToRgb(this.hsb);
            this._updateUI();
            this._updateMarker();
            if (type === "h" && this._drawMesh) this._drawMesh(this.hsb.h);
        }
    }

    _updateFromRGB() {
        this.hsb = this._rgbToHsb(this.value);
        this._updateUI();
        this._updateMarker();
        if (this._drawMesh) this._drawMesh(this.hsb.h);
    }

    _updateUI() {
        const preview = this.container.querySelector(".color-preview");
        const textInput = this.container.querySelector(".color-input input");
        const hexInput = this.container.querySelector(".hex-input");
        const hex = this._hexString(this.value);
        const rgba = this._rgbaString(this.value);

        preview.style.setProperty("--color-preview", rgba);
        textInput.value = hex;
        hexInput.value = hex;

        this.container.querySelectorAll("input[type=range]").forEach(sl => {
            const t = sl.dataset.type;
            if (t in this.value) sl.value = this.value[t];
            if (t in this.hsb) sl.value = this.hsb[t];
        });

        this.container.value = hex;
        if (this.options.onChange) this.options.onChange(hex);
    }

    _parseColor(val) {
        if (!val) return {r: 255, g: 255, b: 255, a: 255};
        const hex = val.replace("#", "").trim();
        if (hex.length === 6 || hex.length === 8) {
            const r = parseInt(hex.substring(0, 2), 16);
            const g = parseInt(hex.substring(2, 4), 16);
            const b = parseInt(hex.substring(4, 6), 16);
            const a = hex.length === 8 ? parseInt(hex.substring(6, 8), 16) : 255;
            return {r, g, b, a};
        }
        return null;
    }

    _hexString(c) {
        const a = this.options.alpha ? c.a.toString(16).padStart(2, "0") : "";
        let hex = (
            "#" +
            c.r.toString(16).padStart(2, "0") +
            c.g.toString(16).padStart(2, "0") +
            c.b.toString(16).padStart(2, "0") +
            (this.options.alpha ? a : "")
        ).toUpperCase();
        if (this.options.alpha && a.toUpperCase() === "FF") hex = hex.substring(0, 7);
        return hex;
    }

    _rgbaString(c) {
        return `rgba(${c.r},${c.g},${c.b},${(c.a / 255).toFixed(3)})`;
    }

    _rgbToHsb({r, g, b}) {
        r /= 255;
        g /= 255;
        b /= 255;
        const v = Math.max(r, g, b);
        const n = v - Math.min(r, g, b);
        const h = n === 0 ? 0 :
            v === r ? (60 * (g - b) / n + 360) % 360 :
                v === g ? (60 * (b - r) / n + 120) :
                    (60 * (r - g) / n + 240);
        const s = v === 0 ? 0 : n / v;
        return {h, s: s * 100, v: v * 100};
    }

    _hsbToRgb({h, s, v}) {
        s /= 100;
        v /= 100;
        const f = (n, k = (n + h / 60) % 6) => v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
        return {
            r: Math.round(f(5) * 255),
            g: Math.round(f(3) * 255),
            b: Math.round(f(1) * 255),
            a: this.value.a
        };
    }

    val(value) {
        this.value = this._parseColor(value);
        this.hsb = this._rgbToHsb(this.value);
        this._updateUI();
    }
}

registerGlobal("ColorDropdown", ColorDropdown);